import { useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';
import { usePage } from '@inertiajs/react';
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import { Selection as ProseMirrorSelection } from '@tiptap/pm/state';
import { useCurrentEditor } from '@tiptap/react';
import { toast } from 'sonner';

import { useSiteAiContext } from '@/components/ai/site-ai-context';
import { AINavigationConfirmDialog } from '@/components/page-editor/ai-navigation-confirm-dialog';
import { AI_TOAST_MESSAGES } from '@/components/shared/ai-menu-constants';
import { useStreamingCompletion } from '@/hooks/mastra/use-streaming-completion';
import { isRichTextToolbarEngaged } from '@/lib/editor-portal-targets';
import { addWindowEventListener } from '@/lib/utils';
import { TaskType } from '@/types/assistant';
import type { BasePageProps } from '@/types/page';
import type { SharedData } from '@/types/shared';
import { Prompt } from './prompt';
import {
  captureSelectionContext,
  formatAiContent,
  getHtmlBefore,
  type SelectionContext,
} from './selection-context';
import { StreamingState } from './streaming-state';

interface GenerativeMenuProps {
  onClose: () => void;
}

interface PageEditorPageProps extends SharedData, BasePageProps {
  accountId: string;
}

export function GenerativeMenu({ onClose }: GenerativeMenuProps) {
  const { editor } = useCurrentEditor();
  const { preferences, firmContext } = useSiteAiContext();
  const { currentUser, accountId } = usePage<PageEditorPageProps>().props;

  const highlightRangeRef = useRef<{ from: number; to: number } | null>(null);
  const selectionContextRef = useRef<SelectionContext | null>(null);
  const lastContentRef = useRef<string>('');

  const {
    streamingState,
    streamedContent,
    isStreaming,
    error,
    startOperation,
    retry,
    cancel,
    discard: resetAiState,
    navigationDialog,
  } = useStreamingCompletion<SelectionContext>({
    userId: currentUser.id,
    accountId,
    preferences,
    firmContext,
    validateContent: (content: string) => {
      if (!content.trim()) {
        toast.error(AI_TOAST_MESSAGES.NO_TEXT_SELECTED);
        return false;
      }
      return true;
    },
  });

  const clearSelectionRefs = useCallback(() => {
    highlightRangeRef.current = null;
    selectionContextRef.current = null;
  }, []);

  const handleAIAction = useCallback(
    async (task: TaskType, title: string, selectionCtx?: SelectionContext) => {
      if (!editor) return;

      const ctx = selectionContextRef.current;
      let metadata: SelectionContext | undefined;

      if (task === TaskType.CONTINUE_WRITING) {
        const to = highlightRangeRef.current?.to ?? editor.state.selection.to;
        metadata = {
          html: getHtmlBefore(editor, to),
          from: highlightRangeRef.current?.from ?? editor.state.selection.from,
          to,
          mode: ctx?.mode === 'singleListItem' ? 'singleListItem' : 'blockRange',
        };
      } else {
        metadata = selectionCtx ?? ctx ?? undefined;
      }

      const content = metadata?.html ?? '';
      lastContentRef.current = content;
      await startOperation({ task, content, title, metadata });
    },
    [editor, startOperation],
  );

  const handleCustomPrompt = useCallback(
    async (prompt: string) => {
      if (!prompt.trim()) {
        toast.error(AI_TOAST_MESSAGES.NO_PROMPT);
        return;
      }

      const ctx = selectionContextRef.current;
      const content = ctx ? `${prompt}\n\n${ctx.html}` : '';
      lastContentRef.current = content;

      await startOperation({
        task: TaskType.CUSTOM,
        content,
        title: prompt,
        metadata: ctx ?? undefined,
      });
    },
    [startOperation],
  );

  const finishAndClose = useCallback(() => {
    resetAiState();
    onClose();
  }, [resetAiState, onClose]);

  const handleReplace = useCallback(() => {
    if (!editor || !streamingState.metadata || !streamedContent.trim()) return;

    const { from, to, mode } = streamingState.metadata;
    const ownerDoc = editor.view.dom.ownerDocument;
    const formattedContent = formatAiContent(streamedContent, mode, ownerDoc);

    if (mode === 'document' || from < 1) {
      // Whole-document replacement — setContent is simpler and avoids position arithmetic.
      // setContent leaves the full doc selected; collapse to the start explicitly.
      editor.chain().focus().setContent(formattedContent).setTextSelection(1).run();
    } else {
      editor
        .chain()
        .focus()
        .unsetAIHighlight()
        .command(({ tr, state }) => {
          // Parse the AI HTML into a ProseMirror Slice using the selection's $from
          // position as context. parseSlice inspects the ancestor chain at $from and
          // produces openStart/openEnd values that match the depth of the selection,
          // so replaceRange works for every case with one code path:
          //   • inline selection (bold mid-paragraph) → open slice, inline replace
          //   • block selection (paragraph, heading)   → closed slice, block replace
          //   • list item(s)                           → correct depth, list replace
          const div = ownerDoc.createElement('div');
          div.innerHTML = formattedContent;
          const resolvedFrom = tr.doc.resolve(Math.max(1, from));
          const slice = ProseMirrorDOMParser.fromSchema(state.schema).parseSlice(div, {
            context: resolvedFrom,
            preserveWhitespace: 'full',
          });
          tr.replaceRange(from, to, slice);
          // Collapse cursor to end of the inserted content so the replaced range
          // is not left selected after the operation.
          const insertEnd = tr.mapping.map(to);
          tr.setSelection(ProseMirrorSelection.near(tr.doc.resolve(insertEnd)));
          return true;
        })
        .run();
    }

    clearSelectionRefs();
    toast.success(AI_TOAST_MESSAGES.CONTENT_REPLACED);
    finishAndClose();
  }, [editor, streamingState.metadata, streamedContent, clearSelectionRefs, finishAndClose]);

  const handleInsertBelow = useCallback(() => {
    if (!editor || !streamingState.metadata || !streamedContent.trim()) return;

    const { to, mode } = streamingState.metadata;
    const ownerDoc = editor.view.dom.ownerDocument;
    const formattedContent = formatAiContent(streamedContent, mode, ownerDoc);

    editor.chain().focus().unsetAIHighlight().insertContentAt(to, formattedContent).run();

    clearSelectionRefs();
    toast.success(AI_TOAST_MESSAGES.CONTENT_INSERTED);
    finishAndClose();
  }, [editor, streamingState.metadata, streamedContent, clearSelectionRefs, finishAndClose]);

  const handleDiscard = useCallback(() => {
    editor?.chain().unsetAIHighlight().run();
    resetAiState();
  }, [resetAiState, editor]);

  const handleRetry = useCallback(() => {
    retry(lastContentRef.current);
  }, [retry]);

  const handleCancel = useCallback(() => {
    editor?.chain().unsetAIHighlight().run();
    cancel();
  }, [editor, cancel]);

  const handleEscape = useEffectEvent((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (isStreaming) {
        handleCancel();
      } else {
        finishAndClose();
      }
    }
  });

  useEffect(() => {
    if (!editor) return;

    const { from, to, empty } = editor.state.selection;

    if (!empty) {
      const selectionContext = captureSelectionContext(editor, from, to);
      selectionContextRef.current = selectionContext;
      highlightRangeRef.current = { from, to };

      if (selectionContext.mode !== 'document') {
        editor.chain().setAIHighlight({ color: 'var(--ai-highlight)' }).run();
      }
    }

    return () => {
      if (highlightRangeRef.current && editor) {
        if (highlightRangeRef.current.from > 0) {
          editor
            .chain()
            .setTextSelection({
              from: highlightRangeRef.current.from,
              to: highlightRangeRef.current.to,
            })
            .unsetAIHighlight()
            .run();
        } else {
          editor.chain().unsetAIHighlight().run();
        }
        clearSelectionRefs();
      }
    };
  }, [editor, clearSelectionRefs]);

  const handleSelectionUpdate = useEffectEvent(() => {
    if (!editor || !editor.state.selection.empty) return;

    if (
      isRichTextToolbarEngaged(editor, {
        sessionActive: streamingState.isOpen || isStreaming,
        includeEditorFocus: false,
      })
    ) {
      return;
    }

    editor.chain().unsetAIHighlight().run();
    onClose();
  });

  useEffect(() => {
    if (!editor) return;

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      if (editor && !editor.isDestroyed) {
        editor.off('selectionUpdate', handleSelectionUpdate);
      }
    };
  }, [editor]);

  const prevMenuStateRef = useRef({ isOpen: streamingState.isOpen, isStreaming, error });
  useLayoutEffect(() => {
    const prev = prevMenuStateRef.current;
    const changed =
      prev.isOpen !== streamingState.isOpen ||
      prev.isStreaming !== isStreaming ||
      prev.error !== error;

    prevMenuStateRef.current = { isOpen: streamingState.isOpen, isStreaming, error };

    if (changed && editor) {
      editor.commands.setMeta('bubbleMenu', 'updatePosition');
    }
  });

  useEffect(() => {
    const cleanup = addWindowEventListener<KeyboardEvent>('keydown', handleEscape);
    return cleanup ?? undefined;
  }, []);

  useEffect(() => {
    return () => {
      editor?.chain().unsetAIHighlight().run();
      clearSelectionRefs();
    };
  }, [editor, clearSelectionRefs]);

  if (!editor) return null;

  const normalizedStreamedContent =
    streamingState.metadata && streamedContent.trim()
      ? formatAiContent(
          streamedContent,
          streamingState.metadata.mode,
          editor.view.dom.ownerDocument,
        )
      : '';

  return (
    <>
      <div className="w-128">
        {!streamingState.isOpen ? (
          <Prompt onSubmit={handleCustomPrompt} onQuickAction={handleAIAction} />
        ) : (
          <div className="p-4">
            <StreamingState
              title={streamingState.title}
              streamedContent={normalizedStreamedContent}
              isStreaming={isStreaming}
              error={error}
              onReplace={handleReplace}
              onInsertBelow={handleInsertBelow}
              onDiscard={handleDiscard}
              onCancel={handleCancel}
              onRetry={handleRetry}
            />
          </div>
        )}
      </div>

      <AINavigationConfirmDialog navigationDialog={navigationDialog} />
    </>
  );
}
